import type { AdminAsset, JournalPostSummary } from '@diana/contracts';
import { ArrowLeft, ImageIcon, Plus } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge, Button } from '../../ui/index.js';
import './JournalList.css';

export function JournalList({
  posts,
  assets,
  onCreate,
  canCreate = true,
}: {
  posts: readonly JournalPostSummary[];
  assets: readonly AdminAsset[];
  onCreate(): Promise<void>;
  canCreate?: boolean;
}) {
  const creating = useRef(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const availableCovers = new Set(assets.map((asset) => asset.id));

  async function createPost() {
    if (!canCreate || creating.current) return;
    creating.current = true;
    setPending(true);
    setError('');
    try {
      await onCreate();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not create post. Try again.');
    } finally {
      creating.current = false;
      setPending(false);
    }
  }

  return (
    <article className="editor journal-list">
      <header className="journal-list__heading">
        <Link className="dui-back-link journal-list__back" to="/site">
          <ArrowLeft size={16} aria-hidden="true" />
          <span>Back to Site</span>
        </Link>
        <h1>Journal</h1>
        <Button
          variant="primary"
          leadingIcon={Plus}
          loading={pending}
          disabled={!canCreate}
          onClick={() => void createPost()}
        >
          New post
        </Button>
      </header>
      {!canCreate ? (
        <p className="journal-list__note">Add at least one active Media asset first.</p>
      ) : null}
      {error ? (
        <p className="journal-list__error" role="alert">
          {error}
        </p>
      ) : null}
      {posts.length === 0 ? (
        <div className="journal-list__empty">
          <h2>No posts yet</h2>
          <p>Create your first post to begin a story.</p>
        </div>
      ) : (
        <div className="journal-list__groups">
          {(['AdminOnly', 'Public'] as const).map((access) => {
            const group = posts.filter((post) => post.siteAccess === access);
            if (group.length === 0) return null;
            const label = access === 'AdminOnly' ? 'Private drafts' : 'Public drafts';
            return (
              <section
                className="journal-list__group"
                key={access}
                aria-labelledby={`journal-list-${access}`}
              >
                <h2 id={`journal-list-${access}`}>{label}</h2>
                <div className="journal-list__rows">
                  {group.map((post) => (
                    <Link className="journal-list__row" to={`/journal/${post.id}`} key={post.id}>
                      <span className="journal-list__cover">
                        {availableCovers.has(post.payload.cover.assetId) ? (
                          <img
                            src={`/api/v1/admin/assets/${post.payload.cover.assetId}/preview`}
                            alt=""
                            loading="lazy"
                          />
                        ) : (
                          <span
                            className="journal-list__placeholder"
                            role="img"
                            aria-label="No cover image"
                          >
                            <ImageIcon size={24} aria-hidden="true" />
                          </span>
                        )}
                      </span>
                      <div className="journal-list__copy">
                        <h3>{post.payload.title}</h3>
                        <div className="journal-list__meta">
                          <Badge>{post.payload.category}</Badge>
                          <span>{post.payload.dateLabel}</span>
                        </div>
                        <span className="journal-list__route">{post.route}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </article>
  );
}
